import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CalendarClock, GraduationCap, Wallet, Heart } from "lucide-react";
import { Reveal } from "@/components/site/reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: CalendarClock, label: "Flexible schedules that fit your life" },
  { icon: GraduationCap, label: "Paid training & ongoing support" },
  { icon: Wallet, label: "Competitive pay, paid on time" },
  { icon: Heart, label: "Work that truly matters" },
];

const POSITIONS = ["Caregiver", "Certified Nursing Assistant (CNA)", "Companion / Homemaker", "Other"];
const AVAILABILITY = ["Full-time", "Part-time", "Weekends", "Evenings & overnights"];
const CERTS = ["CNA", "CPR / First Aid", "Home Health Aide (HHA)", "None yet"];

const inputCls =
  "w-full bg-white border border-silver px-5 py-4 text-sm text-navy-dark placeholder:text-navy-dark/40 focus:outline-none focus:border-navy transition-colors duration-300";

export default function Careers() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", availability: "", message: "" });
  const [certs, setCerts] = useState([]);
  const [license, setLicense] = useState("");
  const [references, setReferences] = useState("");
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleCert = (c) =>
    setCerts((list) => (list.includes(c) ? list.filter((x) => x !== c) : [...list, c]));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/applications`, {
        ...form,
        certifications: certs.join(", "),
        drivers_license: license,
        references,
      });
      toast.success("Thank you for applying — Karen's team will reach out if it's a fit.");
      setForm({ name: "", email: "", phone: "", position: "", availability: "", message: "" });
      setCerts([]);
      setLicense("");
      setReferences("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again or call us.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="careers" data-testid="careers-section" className="bg-silver-light/50 border-y border-silver py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <Reveal>
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-navy" />
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-navy">Join our team</span>
            </div>
            <h2 className="font-display font-light tracking-tight leading-[1.05] text-navy-dark text-4xl md:text-6xl">
              Care for others, <em className="italic text-navy">with us</em>
            </h2>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-navy-dark/70 max-w-md">
              We're always looking for warm, dependable caregivers across Oakland County.
              If you love helping people and want work that means something, tell us about
              yourself below.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-12 space-y-5">
            {PERKS.map((p) => (
              <div key={p.label} className="flex items-center gap-4 text-navy-dark/80">
                <p.icon className="w-4 h-4 text-navy" />
                <span className="text-sm tracking-wide">{p.label}</span>
              </div>
            ))}
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            data-testid="careers-form"
            onSubmit={submit}
            className="bg-white text-navy-dark border border-silver shadow-[0_30px_60px_-30px_rgba(16,43,85,0.2)] p-8 md:p-12 space-y-6"
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-navy-dark/50">
              Employment application
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <input
                data-testid="careers-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className={inputCls}
              />
              <input
                data-testid="careers-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Email address"
                className={inputCls}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <input
                data-testid="careers-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Phone (optional)"
                className={inputCls}
              />
              <select
                data-testid="careers-position-select"
                required
                value={form.position}
                onChange={set("position")}
                className={`${inputCls} appearance-none`}
              >
                <option value="">Position applying for</option>
                {POSITIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <select
              data-testid="careers-availability-select"
              value={form.availability}
              onChange={set("availability")}
              className={`${inputCls} appearance-none`}
            >
              <option value="">Your availability</option>
              {AVAILABILITY.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-navy-dark/50 mb-3">
                Certifications (select all that apply)
              </p>
              <div className="flex flex-wrap gap-3">
                {CERTS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`careers-cert-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => toggleCert(c)}
                    className={`text-xs font-semibold px-4 py-2.5 border transition-colors duration-300 ${
                      certs.includes(c)
                        ? "bg-navy text-white border-navy"
                        : "bg-white text-navy-dark/70 border-silver hover:border-navy"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <select
              data-testid="careers-license-select"
              required
              value={license}
              onChange={(e) => setLicense(e.target.value)}
              className={`${inputCls} appearance-none`}
            >
              <option value="">Driver's license & reliable transportation?</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            <textarea
              data-testid="careers-references-input"
              rows={3}
              value={references}
              onChange={(e) => setReferences(e.target.value)}
              placeholder="References (optional): names & phone numbers of two people we may contact…"
              className={`${inputCls} resize-none`}
            />
            <textarea
              data-testid="careers-message-input"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us about your caregiving experience and why you'd be a great fit…"
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="careers-submit-button"
              type="submit"
              disabled={sending}
              className="group w-full inline-flex items-center justify-center gap-3 bg-navy text-white text-xs font-bold uppercase tracking-[0.2em] px-8 py-5 hover:bg-navy-dark transition-colors duration-300 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Submit application"}
              {!sending && (
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              )}
            </button>
            <p className="text-xs text-navy-dark/50 leading-relaxed">
              Your application goes straight to Karen's team. We review every single one.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
